import React, { useState, useEffect } from "react";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Chip,
  Box,
  Divider,
} from "@mui/material";

const formatDateDisplay = (str) => {
  if (!str) return "N/A";
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split("-");
    return `${d}-${m}-${y}`;
  }
  return str;
};

const ViewUserDetail = ({ open, onClose, userId, equipmentGroupOptions }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;

    const fetchUser = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(
          `${API_URL}/api/get_user_by_id/${userId}`
        );
        const users = res.data?.data || [];
        if (users.length) setUser(users[0]);
      } catch (err) {
        console.error("Failed to fetch user:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [open, userId]);

  const handleClose = () => {
    setUser(null);
    onClose();
  };

  const Row = ({ label, value }) => (
    <div style={{ display: "flex", gap: "12px", padding: "8px 0" }}>
      <Typography
        variant="body2"
        sx={{ color: "#64748b", minWidth: "160px", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: "#1e293b" }}>
        {value || "N/A"}
      </Typography>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          User Details
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {loading && (
          <Typography variant="body2" sx={{ color: "#94a3b8", py: 2 }}>
            Loading...
          </Typography>
        )}

        {!loading && user && (
          <>
            {/* Basic Info */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#475569", mb: 1 }}
            >
              Basic Information
            </Typography>
            <Row label="Name" value={user.users_name} />
            <Row label="Email" value={user.users_email} />
            <Row label="Phone Number" value={user.contact_number} />
            <Row label="Aadhar Number" value={user.aadhar_number} />
            <Row label="Role" value={user.role || user.role_name} />

            <Divider sx={{ my: 2 }} />

            {/* Groups */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#475569", mb: 1 }}
            >
              Groups
            </Typography>
            {user.assigned_group?.length ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {user.assigned_group.map((g) => (
                  <Chip
                    key={g.group_id}
                    label={g.group_name}
                    size="small"
                    sx={{ backgroundColor: "#f1f5f9" }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No groups assigned
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Departments */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#475569", mb: 1 }}
            >
              Designations
            </Typography>
            {user.assigned_departments?.length ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {user.assigned_departments.map((d) => (
                  <Chip
                    key={d.department_id}
                    label={d.department_name}
                    size="small"
                    sx={{ backgroundColor: "#f1f5f9" }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No designations assigned
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Projects */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#475569", mb: 1 }}
            >
              Projects
            </Typography>
            {user.assigned_projects?.length ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {user.assigned_projects.map((p) => (
                  <Box
                    key={p.project_id}
                    sx={{
                      p: 1.5,
                      border: "1px solid #e2e8f0",
                      borderRadius: 1.5,
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {p.project_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      {formatDateDisplay(p.access_start)} →{" "}
                      {formatDateDisplay(p.access_end)}
                    </Typography>
                    {p.equipment_groups?.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mt: 1,
                        }}
                      >
                        {p.equipment_groups.map((eg) => {
                            
                            const option = equipmentGroupOptions.find(opt => opt.value === eg.equipment_group_id);
                            return (
                          <Chip
                            key={eg.equipment_group_id}
                            label={option?.label || eg.equipment_group_code}
                            size="small"
                            sx={{ backgroundColor: "#e0f2fe", fontSize: "11px" }}
                          />
                        )})}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No projects assigned
              </Typography>
            )}
          </>
        )}

        {!loading && !user && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No user data found.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewUserDetail;
